import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3, Building2, CalendarDays, Home, LifeBuoy, Sparkles,
} from "lucide-react";
import AdminLayout from "@/components/layout-admin";
import LumiBriefing from "@/components/lumi-briefing";
import LumiInsights from "@/components/lumi-insights";
import { OnboardingChecklist } from "@/components/org-onboarding-wizard";
import { useAuth } from "@/hooks/use-auth";
import {
  useAdminReservations, useAdminSubmissions, useApartments, useBuildings,
  useCleaningJobs, useCommissionSettings,
} from "@/hooks/use-resources";
import type { CleaningJob } from "@shared/routes";
import type { DateRangeValue } from "@/components/dashboard";
import {
  DashboardHeader, type DashboardBusinessMode,
} from "./admin-dashboard/dashboard-header";
import { DashboardPeriodFilter } from "./admin-dashboard/dashboard-period-filter";
import { DashboardQuickActions, type QuickAction } from "./admin-dashboard/dashboard-quick-actions";
import { PropertyModeView } from "./admin-dashboard/property-mode-view";
import { ServicesModeView } from "./admin-dashboard/services-mode-view";

function isToday(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
}

function isInRange(dateStr: string | Date | null | undefined, from: Date | null, to: Date | null): boolean {
  if (!from && !to) return true;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  // existing field on user record; cast preserves prior behavior until typed
  const hideFinancials = user?.role === "staff"
    && ((user as { disabledFeatures?: string[] } | null)?.disabledFeatures || []).includes("finance_core");

  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });
  const [businessMode, setBusinessMode] = useState<DashboardBusinessMode>("property");
  const [modeInitialized, setModeInitialized] = useState(false);

  const { data: orgInfo } = useQuery<{ id: number; name: string; businessMode: string } | null>({
    queryKey: ["/api/admin/org-info"],
  });

  // Hybrid orgs run BOTH PM and services workflows; default to property but the
  // toggle is always available so the operator can swap views as needed.
  const isHybrid = orgInfo?.businessMode === "hybrid";

  useEffect(() => {
    if (orgInfo && !modeInitialized) {
      if (orgInfo.businessMode === "service_business") setBusinessMode("services");
      else setBusinessMode("property"); // covers property_management AND hybrid (default)
      setModeInitialized(true);
    }
  }, [orgInfo, modeInitialized]);

  const { data: buildings } = useBuildings();
  const { data: apartments } = useApartments();
  const { data: submissions } = useAdminSubmissions();
  const { data: reservations = [] } = useAdminReservations();
  const { data: cleaningJobsData } = useCleaningJobs();
  const cleaningJobsRaw = useMemo<CleaningJob[]>(
    () => (cleaningJobsData?.jobs ?? []) as CleaningJob[],
    [cleaningJobsData],
  );
  const { data: financialRecords = [] } = useQuery<Array<Record<string, unknown>>>({
    queryKey: ["/api/admin/finances"],
    enabled: !hideFinancials,
  });
  const { data: maintenanceJobs = [] } = useQuery<Array<Record<string, unknown>>>({ queryKey: ["/api/maintenance"] });
  const { data: tickets = [] } = useQuery<Array<Record<string, unknown>>>({ queryKey: ["/api/tickets"] });
  const { data: usersData = [] } = useQuery<Array<Record<string, unknown>>>({ queryKey: ["/api/admin/users"] });
  const { data: commissionSettings } = useCommissionSettings();
  const { data: orgFeatures } = useQuery<Array<{ featureKey: string; enabled: boolean }>>({
    queryKey: ["/api/admin/features"],
  });
  const lumiEnabled = !orgFeatures || orgFeatures.some(f => f.featureKey === "lumi_ai" && f.enabled);
  const { data: supplyCostData } = useQuery<{ costByBedrooms: Record<number, number>; itemCount: number }>({
    queryKey: ["/api/admin/supply-catalog/cost-per-job"],
  });
  const { data: lowStockData } = useQuery<{
    lowStockCount: number;
    items: Array<{ id: number; name: string; currentStock: number; reorderPoint: number }>;
  }>({ queryKey: ["/api/admin/inventory/low-stock"] });

  // Period basis: service date (record_date) with created_at fallback — the same basis
  // /api/admin/reports and PM invoices use, so dashboard and reports agree.
  const filteredFinancials = useMemo(
    () => financialRecords.filter(r => isInRange((r.recordDate ?? r.createdAt) as string | Date | null, dateRange.from, dateRange.to)),
    [financialRecords, dateRange],
  );
  const filteredReservations = useMemo(
    () => (reservations as Array<Record<string, unknown>>).filter(r => isInRange(r.checkInDate as string | null, dateRange.from, dateRange.to)),
    [reservations, dateRange],
  );
  const filteredSubmissions = useMemo(
    () => (submissions || []).filter(s => isInRange(s.timestamp, dateRange.from, dateRange.to)),
    [submissions, dateRange],
  );
  // Jobs belong to the period they are SCHEDULED in (iCal creates them weeks ahead);
  // createdAt is only a fallback for legacy jobs. Matches /api/admin/reports.
  const filteredCleaningJobs = useMemo(
    () => cleaningJobsRaw.filter(j => isInRange((j.scheduledDate ?? j.createdAt) as unknown as string | Date | null, dateRange.from, dateRange.to)),
    [cleaningJobsRaw, dateRange],
  );
  const filteredTickets = useMemo(
    () => tickets.filter(t => isInRange(t.createdAt as string | null, dateRange.from, dateRange.to)),
    [tickets, dateRange],
  );
  const filteredMaintenance = useMemo(
    () => maintenanceJobs.filter(j => isInRange(j.createdAt as string | null, dateRange.from, dateRange.to)),
    [maintenanceJobs, dateRange],
  );

  const arrivals = (reservations as Array<{ id: number; guestName: string; checkInDate: string }>)
    .filter(r => isToday(r.checkInDate));
  const departures = (reservations as Array<{ id: number; guestName: string; checkOutDate: string }>)
    .filter(r => isToday(r.checkOutDate));
  const urgentJobs = cleaningJobsRaw
    .filter(j => j.priority === "urgent" && j.status !== "completed")
    .map(j => ({ id: j.id, apartmentId: j.apartmentId, status: j.status }));

  const completedCleanings = filteredCleaningJobs.filter(j => j.status === "completed").length;
  const pendingCleanings = filteredCleaningJobs.filter(j => j.status === "pending").length;
  const inProgressCleanings = filteredCleaningJobs.filter(j => j.status === "in_progress").length;
  const assignedCleanings = filteredCleaningJobs.filter(j => j.assignedTo).length;
  const unassignedCleanings = filteredCleaningJobs.filter(
    j => !j.assignedTo && j.status !== "completed" && j.status !== "cancelled",
  ).length;
  const todayCleanings = cleaningJobsRaw.filter(j => isToday((j.scheduledDate ?? j.createdAt) as unknown as string | null)).length;

  const cleaningFinancials = filteredFinancials.filter(r => r.type === "cleaning" && r.status !== "cancelled");
  const maintenanceFinancials = filteredFinancials.filter(r => r.type === "maintenance" && r.status !== "cancelled");

  const sumNum = (arr: Array<Record<string, unknown>>, field: string) =>
    arr.reduce((s, r) => s + (Number(r[field]) || 0), 0);

  const totalCleaningIncome = sumNum(cleaningFinancials, "totalAmount");
  const totalCleanerPayroll = sumNum(cleaningFinancials, "cleanerAmount");
  const totalPartnerCommissions = sumNum(cleaningFinancials, "partnerAmount");
  const totalMaintenanceIncome = sumNum(maintenanceFinancials, "totalAmount");
  const checkInIncome = sumNum(
    filteredFinancials.filter(r => r.type === "check_in" && r.status !== "cancelled"),
    "totalAmount",
  );
  const grossServiceIncome = totalCleaningIncome + totalMaintenanceIncome + checkInIncome;

  const revenueTypes = ["check_in", "booking_commission", "unit_setup", "cleaning", "maintenance", "miscellaneous"];
  const revenueRecords = filteredFinancials.filter(r => r.status !== "cancelled" && revenueTypes.includes(r.type as string));
  const totalRevenue = sumNum(revenueRecords, "totalAmount");
  const bookingRevenue = (filteredReservations as Array<{ status: string; paidAmount?: string }>)
    .filter(r => r.status !== "cancelled" && parseFloat(r.paidAmount || "0") > 0)
    .reduce((sum, r) => sum + parseFloat(r.paidAmount || "0"), 0);
  const combinedRevenue = Math.max(totalRevenue, bookingRevenue);
  const pendingPayments = sumNum(revenueRecords.filter(r => r.status === "pending"), "totalAmount");
  const totalPaid = sumNum(revenueRecords.filter(r => r.status === "paid"), "totalAmount");

  const now = new Date();
  const activeReservations = (filteredReservations as Array<{ checkInDate: string; checkOutDate: string }>).filter(r => {
    const checkIn = new Date(r.checkInDate);
    const checkOut = new Date(r.checkOutDate);
    return checkIn <= now && now <= checkOut;
  }).length;

  const openTickets = filteredTickets.filter(t => t.status === "open" || t.status === "in_progress").length;
  const activeMaintenance = filteredMaintenance.filter(j => j.status !== "completed" && j.status !== "cancelled").length;

  const apartmentsList = useMemo<Array<{ id: number; name?: string; bedrooms?: number }>>(
    () => (apartments || []) as Array<{ id: number; name?: string; bedrooms?: number }>,
    [apartments],
  );

  const revenueByApartment = useMemo(() => {
    const totals = filteredFinancials.reduce<Record<string, number>>((acc, r) => {
      const key = r.apartmentId as string | number | undefined;
      if (!key) return acc;
      acc[String(key)] = (acc[String(key)] || 0) + (Number(r.totalAmount) || 0);
      return acc;
    }, {});
    return Object.entries(totals)
      .map(([id, total]) => {
        const apt = apartmentsList.find(a => String(a.id) === id);
        return { id, name: apt?.name || `Apt #${id}`, total };
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [filteredFinancials, apartmentsList]);

  const hasCatalogData = !!(supplyCostData && supplyCostData.itemCount > 0);

  const supplyCostByBedrooms = useMemo<Record<number, number>>(() => {
    if (hasCatalogData && supplyCostData) return supplyCostData.costByBedrooms;
    const s = commissionSettings as Record<string, unknown> | undefined;
    if (!s) return { 0: 8, 1: 10, 2: 15, 3: 20 };
    return {
      0: Number(s.supplyCostStudio || 8),
      1: Number(s.supplyCost || 10),
      2: Number(s.supplyCost2br || 15),
      3: Number(s.supplyCost3br || 20),
    };
  }, [commissionSettings, supplyCostData, hasCatalogData]);

  const incomeByApartment = useMemo(() => {
    const aptMap: Record<string, {
      id: string; name: string; bedrooms: number;
      cleaningIncome: number; cleanerPay: number; partnerPay: number;
      maintenanceIncome: number; jobCount: number; supplyCost: number;
    }> = {};

    cleaningFinancials.forEach(r => {
      const aptId = String(r.apartmentId || "unknown");
      if (!aptMap[aptId]) {
        const apt = apartmentsList.find(a => String(a.id) === aptId);
        const bedrooms = apt?.bedrooms || 1;
        aptMap[aptId] = {
          id: aptId, name: apt?.name || `Apt #${aptId}`, bedrooms,
          cleaningIncome: 0, cleanerPay: 0, partnerPay: 0,
          maintenanceIncome: 0, jobCount: 0, supplyCost: 0,
        };
      }
      const row = aptMap[aptId];
      row.cleaningIncome += Number(r.totalAmount) || 0;
      row.cleanerPay += Number(r.cleanerAmount) || 0;
      row.partnerPay += Number(r.partnerAmount) || 0;
      row.jobCount += 1;
      row.supplyCost += supplyCostByBedrooms[Math.min(row.bedrooms, 3)] || 10;
    });

    maintenanceFinancials.forEach(r => {
      const aptId = String(r.apartmentId || "unknown");
      if (!aptMap[aptId]) {
        const apt = apartmentsList.find(a => String(a.id) === aptId);
        aptMap[aptId] = {
          id: aptId, name: apt?.name || `Apt #${aptId}`, bedrooms: apt?.bedrooms || 1,
          cleaningIncome: 0, cleanerPay: 0, partnerPay: 0,
          maintenanceIncome: 0, jobCount: 0, supplyCost: 0,
        };
      }
      aptMap[aptId].maintenanceIncome += Number(r.totalAmount) || 0;
    });

    return Object.values(aptMap)
      .sort((a, b) => (b.cleaningIncome + b.maintenanceIncome) - (a.cleaningIncome + a.maintenanceIncome));
  }, [cleaningFinancials, maintenanceFinancials, apartmentsList, supplyCostByBedrooms]);

  const earningsByWorker = useMemo(() => {
    const map: Record<string, { userId: number; name: string; role: string; jobCount: number; totalEarned: number; avgPerJob: number }> = {};
    cleaningFinancials.forEach(r => {
      const uid = r.cleanerUserId as number | undefined;
      if (!uid) return;
      const key = String(uid);
      if (!map[key]) {
        const u = (usersData as Array<{ id: number; fullName?: string; username?: string; role?: string }>).find(x => x.id === uid);
        map[key] = {
          userId: uid,
          name: u?.fullName || u?.username || `User #${uid}`,
          role: u?.role || "cleaner",
          jobCount: 0, totalEarned: 0, avgPerJob: 0,
        };
      }
      map[key].jobCount += 1;
      map[key].totalEarned += Number(r.cleanerAmount) || 0;
    });
    return Object.values(map)
      .map(w => ({ ...w, avgPerJob: w.jobCount > 0 ? w.totalEarned / w.jobCount : 0 }))
      .sort((a, b) => b.totalEarned - a.totalEarned);
  }, [cleaningFinancials, usersData]);

  const totalEstSupplyCost = incomeByApartment.reduce((s, a) => s + a.supplyCost, 0);
  // Real org-absorbed expenses (utilities, salaries) carry a negative adminAmount on org_expense records.
  const totalOrgExpenses = (financialRecords as Array<Record<string, unknown>>)
    .filter(r => r.type === 'org_expense' && r.status !== 'cancelled')
    .reduce((s, r) => s - (parseFloat(String(r.adminAmount ?? '0')) || 0), 0);
  const netServiceProfit = grossServiceIncome - totalCleanerPayroll - totalPartnerCommissions - totalEstSupplyCost - totalOrgExpenses;

  const pricingTiers = useMemo(() => {
    if (!commissionSettings) return [];
    const s = commissionSettings as Record<string, unknown>;
    const num = (k: string, fallback: number) => Number(s[k] || fallback);
    const base = [
      { type: "Studio", fee: num("cleaningFeeStudio", num("cleaningFee", 90)), pay: num("cleanerPayStudio", num("cleanerPay", 60)) },
      { type: "1 BR", fee: num("cleaningFee", 110), pay: num("cleanerPay", 75) },
      { type: "2 BR", fee: num("cleaningFee2br", num("cleaningFee", 140)), pay: num("cleanerPay2br", num("cleanerPay", 95)) },
      { type: "3 BR", fee: num("cleaningFee3br", num("cleaningFee", 170)), pay: num("cleanerPay3br", num("cleanerPay", 115)) },
      { type: "4 BR", fee: num("cleaningFee4br", num("cleaningFee", 200)), pay: num("cleanerPay4br", num("cleanerPay", 140)) },
      { type: "5+ BR", fee: num("cleaningFee5br", num("cleaningFee", 240)), pay: num("cleanerPay5br", num("cleanerPay", 165)) },
    ];
    const dynamic = ((s.tiers as Array<{ bedrooms: number; cleaningFee: number; cleanerPay: number }>) ?? [])
      .map(t => ({ type: `${t.bedrooms} BR`, fee: Number(t.cleaningFee), pay: Number(t.cleanerPay) }));
    return [...base, ...dynamic];
  }, [commissionSettings]);

  const partnerName = (commissionSettings as { partnerName?: string } | undefined)?.partnerName || "Partner";
  const partnerCommissionPct = String((commissionSettings as { partnerCommissionPct?: string | number } | undefined)?.partnerCommissionPct || "14");

  const recentActivity = (filteredSubmissions || []).map(s => ({
    id: s.id,
    guestName: s.guestName,
    timestamp: s.timestamp,
  }));

  // ---- Daily-buckets time series for hero chart + KPI sparklines (last 30 days) ----
  const trendBuckets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days: string[] = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      days.push(d.toISOString().slice(0, 10));
    }
    const empty = () => Object.fromEntries(days.map(d => [d, 0])) as Record<string, number>;
    const rev = empty();
    const bookings = empty();
    const jobs = empty();
    for (const r of revenueRecords) {
      const dt = r.createdAt as string | Date | undefined;
      if (!dt) continue;
      const key = new Date(dt).toISOString().slice(0, 10);
      if (key in rev) rev[key] = (rev[key] ?? 0) + (Number(r.totalAmount) || 0);
    }
    for (const r of filteredReservations as Array<{ checkInDate?: string }>) {
      if (!r.checkInDate) continue;
      const key = new Date(r.checkInDate).toISOString().slice(0, 10);
      if (key in bookings) bookings[key] = (bookings[key] ?? 0) + 1;
    }
    for (const j of filteredSubmissions || []) {
      const dt = j.timestamp;
      if (!dt) continue;
      const key = new Date(dt).toISOString().slice(0, 10);
      if (key in jobs) jobs[key] = (jobs[key] ?? 0) + 1;
    }
    return {
      revenueTrend: days.map(d => ({ date: d, value: rev[d] ?? 0 })),
      bookingsTrend: days.map(d => ({ date: d, value: bookings[d] ?? 0 })),
      jobsTrend: days.map(d => ({ date: d, value: jobs[d] ?? 0 })),
    };
  }, [revenueRecords, filteredReservations, filteredSubmissions]);

  const cleaningStatusBreakdown = useMemo(() => {
    return [
      { name: "Completed", value: completedCleanings, color: "#10b981" },
      { name: "In Progress", value: inProgressCleanings, color: "#3b82f6" },
      { name: "Pending", value: pendingCleanings, color: "#f59e0b" },
      { name: "Unassigned", value: unassignedCleanings, color: "#ef4444" },
    ];
  }, [completedCleanings, inProgressCleanings, pendingCleanings, unassignedCleanings]);

  const bookingSourceBreakdown = useMemo(() => {
    const palette = ["#6366f1", "#8b5cf6", "#22c55e", "#f59e0b", "#ef4444", "#0ea5e9"];
    const counts: Record<string, number> = {};
    for (const r of filteredReservations as Array<{ source?: string; status?: string }>) {
      if (r.status === "cancelled") continue;
      const src = r.source || "manual";
      counts[src] = (counts[src] ?? 0) + 1;
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], i) => ({ name, value, color: palette[i % palette.length]! }));
  }, [filteredReservations]);

  const quickActions: QuickAction[] = [
    { label: "Add New Building", href: "/admin/buildings", icon: Building2 },
    { label: "Manage Apartments", href: "/admin/apartments", icon: Home },
    { label: "View Reservations", href: "/admin/reservations", icon: CalendarDays },
    ...(!hideFinancials ? [{ label: "Financial Reports", href: "/admin/finances", icon: BarChart3 }] : []),
    { label: "Cleaning Jobs", href: "/admin/cleaning-jobs", icon: Sparkles },
    { label: "Support Tickets", href: "/admin/tickets", icon: LifeBuoy },
  ];

  const showServices = !hideFinancials && businessMode === "services";

  return (
    <AdminLayout>
      <div className="space-y-8">
        <OnboardingChecklist />

        <DashboardHeader
          mode={businessMode}
          onModeChange={setBusinessMode}
          hideToggle={hideFinancials}
          hideFinancials={hideFinancials}
          isHybrid={isHybrid}
        />

        {lumiEnabled && <LumiBriefing businessMode={businessMode} />}
        {lumiEnabled && <LumiInsights businessMode={businessMode} />}

        {!hideFinancials && (
          <DashboardPeriodFilter value={dateRange} onChange={setDateRange} />
        )}

        {showServices ? (
          <ServicesModeView
            grossServiceIncome={grossServiceIncome}
            totalCleanerPayroll={totalCleanerPayroll}
            totalPartnerCommissions={totalPartnerCommissions}
            netServiceProfit={netServiceProfit}
            cleaningJobsCount={cleaningFinancials.length}
            partnerName={partnerName}
            completedCleanings={completedCleanings}
            pendingCleanings={pendingCleanings}
            inProgressCleanings={inProgressCleanings}
            assignedCleanings={assignedCleanings}
            unassignedCleanings={unassignedCleanings}
            pricingTiers={pricingTiers}
            incomeByApartment={incomeByApartment}
            earningsByWorker={earningsByWorker}
            totalCleaningIncome={totalCleaningIncome}
            totalMaintenanceIncome={totalMaintenanceIncome}
            checkInIncome={checkInIncome}
            totalEstSupplyCost={totalEstSupplyCost}
            orgExpenses={totalOrgExpenses}
            partnerCommissionPct={partnerCommissionPct}
            hasCatalogData={hasCatalogData}
            cleaningRecordsCount={cleaningFinancials.length}
            maintenanceRecordsCount={maintenanceFinancials.length}
          />
        ) : (
          <PropertyModeView
            hideFinancials={hideFinancials}
            buildingsCount={buildings?.length || 0}
            apartmentsCount={apartmentsList.length}
            reservationsCount={filteredReservations.length}
            checkoutsCount={filteredSubmissions.length}
            totalRevenue={combinedRevenue}
            pendingPayments={pendingPayments}
            totalPaid={totalPaid}
            activeReservations={activeReservations}
            openTickets={openTickets}
            activeMaintenance={activeMaintenance}
            completedCleanings={completedCleanings}
            pendingCleanings={pendingCleanings}
            inProgressCleanings={inProgressCleanings}
            unassignedCleanings={unassignedCleanings}
            assignedCleanings={assignedCleanings}
            todayCleanings={todayCleanings}
            totalCleaningIncome={totalCleaningIncome}
            totalMaintenanceIncome={totalMaintenanceIncome}
            arrivals={arrivals}
            departures={departures}
            urgentJobs={urgentJobs}
            lowStockData={lowStockData}
            revenueByApartment={revenueByApartment}
            recentActivity={recentActivity}
            revenueTrend={trendBuckets.revenueTrend}
            bookingsTrend={trendBuckets.bookingsTrend}
            jobsTrend={trendBuckets.jobsTrend}
            cleaningStatusBreakdown={cleaningStatusBreakdown}
            bookingSourceBreakdown={bookingSourceBreakdown}
          />
        )}

        <DashboardQuickActions actions={quickActions} />
      </div>
    </AdminLayout>
  );
}
